import { c as _c } from "react/compiler-runtime";
import type { ItemQuantityPriceDto, ProductPublicDto } from "@sticker-giant/sg-frontend-common";
import { usePdpStore } from "@/stores/usePdpStore";
import { useCustomQuantityPricing, useQuantityPricing } from "./useQuantityPricing";
interface SelectedQuantityPrice {
  quantityPrice?: ItemQuantityPriceDto;
  isTrusted: boolean;
}

/**
 * A hook that resolves the quantityPrice and whether that quantityPrice is non-stale data based on the
 * currently selected quantity and data returned from the pricing service.
 *
 * @returns An object containing:
 *   - `quantityPrice` (optional): The resolved quantity price for the selected quantity.
 *   - `isTrusted`: A boolean indicating whether the resolved quantity price is non-stale data.
 */
export function useSelectedQuantityPrice(product) {
  const $ = _c(11);
  const selectedQuantity = usePdpStore(_temp);
  const preset = useQuantityPricing(product);
  const custom = useCustomQuantityPricing(product);
  if (selectedQuantity === undefined) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        isTrusted: false
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] !== preset.data?.quantityPrices || $[2] !== selectedQuantity) {
    t0 = preset.data?.quantityPrices.find(price => price.quantity === selectedQuantity);
    $[1] = preset.data?.quantityPrices;
    $[2] = selectedQuantity;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const presetMatch = t0;
  if (presetMatch) {
    const t1 = !preset.isPlaceholderData;
    let t2;
    if ($[4] !== presetMatch || $[5] !== t1) {
      t2 = {
        quantityPrice: presetMatch,
        isTrusted: t1
      };
      $[4] = presetMatch;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  const customMatch = custom.data?.quantityPrices[0];
  if (customMatch) {
    const t1 = !custom.isPlaceholderData;
    let t2;
    if ($[7] !== customMatch || $[8] !== t1) {
      t2 = {
        quantityPrice: customMatch,
        isTrusted: t1
      };
      $[7] = customMatch;
      $[8] = t1;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t1;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      isTrusted: false
    };
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
function _temp(state) {
  return state.quantity;
}